import {
	CanvasData,
	FlowNode,
	PatientTreatmentPlanStageLog,
	detectBackEdges,
} from "../../../../../../types/healthcare/TreatmentPlanTypes";
import { topoSort } from "./helpers";

/**
 * Build an EXECUTION-ORDER index for a plan's stages, keyed by canvas node id (= the snapshot
 * stage_key the logs use).
 *
 * Why not stage_number: the builder's stage_number reflects the order stages were ADDED, not the
 * order they run — a node inserted later in the canvas (e.g. "Patient to complete CoA form") can
 * carry a higher number than a node it precedes in the flow (the CoA upload). Ordering by
 * stage_number therefore shows steps out of sequence.
 *
 * The rule:
 *  - Stages the plan has REACHED (have a stage log) are ordered by when they were entered
 *    (entered_at) — the literal execution order, robust to loops and branches.
 *  - Stages not yet reached follow in topological (flow) order, computed over the canvas with
 *    back-edges (loops) removed so the graph is a DAG.
 *  - Reached stages sort before not-yet-reached ones.
 */
export function buildExecutionOrderIndex(
	canvas: CanvasData | null | undefined,
	stageLogs: PatientTreatmentPlanStageLog[],
): Map<string, number> {
	const nodes = (canvas?.nodes ?? []).filter((n) => n.type !== "section_group");
	const edges = canvas?.edges ?? [];

	const backEdgeIds = detectBackEdges(canvas?.nodes ?? [], edges);
	const forwardEdges = edges.filter((e) => !backEdgeIds.has(e.id));

	const topo = topoSort(nodes as FlowNode[], forwardEdges);
	const topoIndex = new Map<string, number>(topo.map((n, i) => [n.id, i]));

	// Earliest entered_at per stage key (first time the plan reached that node).
	const enteredAt = new Map<string, number>();
	stageLogs.forEach((log) => {
		const key = log.stage_snapshot?.stage_key ?? log.stage?.stage_key;
		if (!key || !log.entered_at) return;
		const t = new Date(log.entered_at).getTime();
		if (!enteredAt.has(key) || t < (enteredAt.get(key) as number)) enteredAt.set(key, t);
	});

	const ordered = topo
		.map((n) => n.id)
		.sort((a, b) => {
			const ea = enteredAt.get(a);
			const eb = enteredAt.get(b);
			if (ea !== undefined && eb !== undefined) return ea - eb; // both reached → by time entered
			if (ea !== undefined) return -1; // reached before not-yet-reached
			if (eb !== undefined) return 1;
			return (topoIndex.get(a) ?? 0) - (topoIndex.get(b) ?? 0); // both future → flow order
		});

	return new Map(ordered.map((id, i) => [id, i]));
}

/**
 * Per-ATTEMPT execution order for a looping plan.
 *
 * Unlike buildExecutionOrderIndex (which collapses every visit of a stage_key to its earliest
 * entered_at, hiding loop re-entries), this ranks each individual stage LOG so iteration-1 loop-body
 * logs order AFTER iteration-0 ones.
 *
 *  - `logOrder`: keyed by log id → the execution rank of that attempt row. All logs are sorted by
 *    the tuple (entered_at asc, loop_iteration asc, topoIndex(stage_key) asc, id asc). Logs with no
 *    entered_at sort last among logs, then by the remaining tuple.
 *  - `nodeOrder`: keyed by node id (stage_key) → the topo rank, used as the fallback ordering for
 *    not-yet-reached (zero-log) future nodes.
 */
export function buildAttemptExecutionOrder(
	canvas: CanvasData | null | undefined,
	stageLogs: PatientTreatmentPlanStageLog[],
): { logOrder: Map<number, number>; nodeOrder: Map<string, number> } {
	const nodes = (canvas?.nodes ?? []).filter((n) => n.type !== "section_group");
	const edges = canvas?.edges ?? [];

	const backEdgeIds = detectBackEdges(canvas?.nodes ?? [], edges);
	const forwardEdges = edges.filter((e) => !backEdgeIds.has(e.id));

	const topo = topoSort(nodes as FlowNode[], forwardEdges);
	const topoIndex = new Map<string, number>(topo.map((n, i) => [n.id, i]));
	const nodeOrder = new Map<string, number>(topo.map((n, i) => [n.id, i]));

	const enteredMs = (log: PatientTreatmentPlanStageLog): number =>
		log.entered_at ? new Date(log.entered_at).getTime() : Number.POSITIVE_INFINITY;

	const ordered = [...stageLogs].sort((a, b) => {
		const ea = enteredMs(a);
		const eb = enteredMs(b);
		if (ea !== eb) return ea - eb; // entered_at asc (no entered_at → +Infinity → last)
		const ia = a.loop_iteration ?? 0;
		const ib = b.loop_iteration ?? 0;
		if (ia !== ib) return ia - ib; // loop_iteration asc
		const ka = a.stage_snapshot?.stage_key ?? a.stage?.stage_key;
		const kb = b.stage_snapshot?.stage_key ?? b.stage?.stage_key;
		const ta = ka !== undefined ? topoIndex.get(ka) ?? Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;
		const tb = kb !== undefined ? topoIndex.get(kb) ?? Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;
		if (ta !== tb) return ta - tb; // topo order of the stage_key
		return (a.id ?? 0) - (b.id ?? 0); // id asc
	});

	const logOrder = new Map<number, number>();
	ordered.forEach((log, i) => {
		if (log.id !== undefined) logOrder.set(log.id, i);
	});

	return { logOrder, nodeOrder };
}
